import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { GlobalPatientSearchDialog } from './GlobalPatientSearchDialog';

/**
 * GP-308 — Global Patient Search.
 *
 * Mounts a single dialog instance at the app root so staff can open it from
 * anywhere (top-nav button or `Ctrl/Cmd + P` hotkey) WITHOUT navigating
 * away from the queue or order they're working on. Dismissing returns them
 * to the exact same view (same route, same scroll, same filters) because
 * the underlying page never unmounts.
 *
 * `Ctrl/Cmd + K` is reserved by the cross-pharmacy palette (GP-314); this
 * provider uses `Ctrl/Cmd + P` to keep the two surfaces clearly separate.
 */

interface GlobalPatientSearchContextValue {
  openPatientSearch: () => void;
  closePatientSearch: () => void;
}

const GlobalPatientSearchContext = createContext<GlobalPatientSearchContextValue>({
  openPatientSearch: () => {},
  closePatientSearch: () => {},
});

export const useGlobalPatientSearch = () => useContext(GlobalPatientSearchContext);

export function GlobalPatientSearchProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  const openPatientSearch = useCallback(() => setOpen(true), []);
  const closePatientSearch = useCallback(() => setOpen(false), []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'p') return;
      // Browser's print shortcut is Ctrl/Cmd+P — we override it inside the
      // app because no one in PHOX is using browser print, and the muscle
      // memory of "P for Patient" is the whole point. preventDefault stops
      // the print dialog.
      e.preventDefault();
      setOpen((prev) => !prev);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <GlobalPatientSearchContext.Provider value={{ openPatientSearch, closePatientSearch }}>
      {children}
      <GlobalPatientSearchDialog open={open} onClose={closePatientSearch} />
    </GlobalPatientSearchContext.Provider>
  );
}
